import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Stack,
  Typography,
  Chip,
  Alert,
  Checkbox,
  Link as JoyLink,
} from '@mui/joy';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { AUTH_TOKEN_NAME } from '../constants';
import { GET_INVITATION_BY_TOKEN } from '../gql/team.queries';
import { ACCEPT_INVITATION_WITH_TOKEN } from '../gql/team.mutations';
import { parseGraphQLError, formatTimestamp } from '../utils';
import { TEAM_ROLE_LABELS, TEAM_ROLE_COLORS, TEAM_ROLE_DESCRIPTIONS } from '../constants';

/**
 * Magic link landing page for team invitations.
 * Route: /invitation/:token
 * 
 * Flow:
 * 1. Extract token from URL
 * 2. Call getInvitationByToken to validate and get invitation details
 * 3. Display invitation info (company, role, inviter)
 * 4. If isNewUser: Show password + name form
 * 5. If existing user: Show simple Accept button
 * 6. On accept: Store authToken and redirect to dashboard
 */
const TeamInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invitation details
  const { data, loading: fetchingInvitation, error: fetchError } = useQuery(GET_INVITATION_BY_TOKEN, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const invitation = data?.getInvitationByToken;

  // Accept invitation mutation
  const [acceptInvitation, { loading: accepting }] = useMutation(ACCEPT_INVITATION_WITH_TOKEN);

  // Handle fetch errors
  useEffect(() => {
    if (fetchError) {
      const message = parseGraphQLError(fetchError);
      if (message.toLowerCase().includes('expired')) {
        setError('This invitation has expired. Please ask the team admin to send a new invitation.');
      } else if (message.toLowerCase().includes('invalid')) {
        setError('This invitation link is invalid or has already been used.');
      } else {
        setError(message);
      }
    }
  }, [fetchError]);

  const handleAccept = async (e) => {
    e?.preventDefault();
    setError(null);

    // Validate password for new users
    if (invitation?.isNewUser) {
      if (!password) {
        setError('Password is required');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (!terms) {
        setError('You must agree to the Terms of Service and Privacy Policy');
        return;
      }
    }

    try {
      const variables = { token };
      if (invitation?.isNewUser) {
        variables.password = password;
        if (firstName) variables.firstName = firstName;
        if (lastName) variables.lastName = lastName;
      }

      const { data: result } = await acceptInvitation({ variables });
      const response = result?.acceptInvitationWithToken;

      if (response?.success && response?.authToken) {
        // Store auth token
        localStorage.setItem(AUTH_TOKEN_NAME, response.authToken);
        setSuccess(true);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        setError(response?.message || 'Failed to accept invitation');
      }
    } catch (err) {
      setError(parseGraphQLError(err));
    }
  };

  const handleDecline = () => {
    navigate('/', { replace: true });
  };

  // Loading state
  if (fetchingInvitation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-md">Loading invitation...</Typography>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.500' }} />
        <Typography level="h3" textAlign="center">
          Welcome to {invitation?.company}!
        </Typography>
        <Typography level="body-md" color="neutral" textAlign="center">
          Redirecting to dashboard...
        </Typography>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  // Error state (no invitation data)
  if (error && !invitation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 64, color: 'danger.500' }} />
        <Typography level="h3" textAlign="center" color="danger">
          Unable to Process Invitation
        </Typography>
        <Typography level="body-md" textAlign="center" sx={{ maxWidth: 400 }}>
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </Box>
    );
  }

  // No token provided
  if (!token) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 64, color: 'danger.500' }} />
        <Typography level="h3" textAlign="center" color="danger">
          Invalid Link
        </Typography>
        <Typography level="body-md" textAlign="center">
          No invitation token provided. Please use the link from your email.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Go to Home
        </Button>
      </Box>
    );
  }

  // Main invitation view
  return (
    <Box
      component="main"
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: 500,
        maxWidth: '100%',
        mx: 'auto',
        px: 2,
      }}
    >
      <Stack sx={{ gap: 3 }}>
        {/* Header */}
        <Stack sx={{ gap: 1 }}>
          <Typography component="h1" level="h3">
            Team Invitation
          </Typography>
          <Typography level="body-sm" color="neutral">
            You&apos;ve been invited to join a team on Cavalry
          </Typography>
        </Stack>

        {/* Invitation Details */}
        <Stack spacing={2.5} sx={{ bgcolor: 'background.level1', p: 3, borderRadius: 'sm' }}>
          <Box>
            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
              Company
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BusinessIcon sx={{ fontSize: 20, color: 'primary.500' }} />
              <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                {invitation?.company}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
              Invited by
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon sx={{ fontSize: 20, color: 'primary.500' }} />
              <Typography level="title-md">
                {invitation?.inviterName || 'Team Admin'}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
              Your Role
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                variant="soft"
                color={TEAM_ROLE_COLORS[invitation?.role]}
                size="md"
              >
                {TEAM_ROLE_LABELS[invitation?.role]}
              </Chip>
              <Typography level="body-sm" color="neutral">
                {TEAM_ROLE_DESCRIPTIONS[invitation?.role]}
              </Typography>
            </Box>
          </Box>

          {invitation?.expiresAt && (
            <Box>
              <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                Expires
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AccessTimeIcon sx={{ fontSize: 20, color: 'neutral.500' }} />
                <Typography level="body-sm">
                  {formatTimestamp(invitation.expiresAt, 'MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {/* Form for new users */}
        {invitation?.isNewUser && (
          <form onSubmit={handleAccept} id="accept-form">
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={invitation?.email || ''}
                  disabled
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>First name</FormLabel>
                  <Input
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    name="firstName"
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    name="lastName"
                  />
                </FormControl>
              </Box>

              <FormControl required>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormControl>

              <FormControl required size="sm">
                <Checkbox
                  label={
                    <>
                      By signing up you agree to our Terms of Service and Privacy Policy.
                    </>
                  }
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                />
                <FormHelperText>
                  <Typography level="body-sm">
                    Read our{' '}
                    <JoyLink href="https://www.cavalryhr.com/terms-of-service" target="_blank">
                      Terms of Service
                    </JoyLink>{' '}
                    and{' '}
                    <JoyLink href="https://www.cavalryhr.com/privacy-policy" target="_blank">
                      Privacy Policy
                    </JoyLink>.
                  </Typography>
                </FormHelperText>
              </FormControl>
            </Stack>
          </form>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleDecline}
            disabled={accepting}
            fullWidth
          >
            Decline
          </Button>
          <Button
            type={invitation?.isNewUser ? 'submit' : 'button'}
            form={invitation?.isNewUser ? 'accept-form' : undefined}
            onClick={invitation?.isNewUser ? undefined : handleAccept}
            loading={accepting}
            fullWidth
          >
            {invitation?.isNewUser ? 'Accept & Join' : 'Accept Invitation'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default TeamInvitation;
